import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ThemeProvider } from "@mui/material/styles";
import { SxProps, Theme } from "@mui/material/styles";
import { GomakeOutlinedFieldClassName } from "./constants";
import {
  getOutlinedAutocompleteSx,
  getOutlinedFieldInputLabelProps,
  getOutlinedFieldInputProps,
} from "./outlined-field-styles";
import { useOutlinedFieldTheme } from "./use-outlined-field-theme";
import { CSSProperties } from "react";

interface GomakeOutlinedAutocompleteProps {
  label: string;
  options: any[];
  value?: any;
  onChange?: (event: any, value: any) => void;
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  disabled?: boolean;
  loading?: boolean;
  disableClearable?: boolean;
  sx?: SxProps<Theme>;
  style?: CSSProperties;
  className?: string;
  size?: "small" | "medium";
  inputHeight?: number;
}

const GomakeOutlinedAutocomplete = ({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  disabled,
  loading,
  disableClearable,
  sx,
  style,
  className,
  size = "small",
  inputHeight = 40,
}: GomakeOutlinedAutocompleteProps) => {
  const { direction, isRTL, theme } = useOutlinedFieldTheme();

  return (
    <ThemeProvider theme={theme}>
      <div
        className={`${GomakeOutlinedFieldClassName}${className ? ` ${className}` : ""}`}
        style={{ width: "100%", ...style }}
        dir={direction}
      >
        <Autocomplete
          fullWidth
          size={size}
          options={options}
          value={value ?? null}
          onChange={onChange}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          disabled={disabled}
          loading={loading}
          disableClearable={disableClearable}
          componentsProps={{
            paper: {
              sx: {
                direction,
                "& .MuiAutocomplete-option": {
                  direction,
                  textAlign: isRTL ? "right" : "left",
                  justifyContent: isRTL ? "flex-end" : "flex-start",
                },
              },
            },
            popper: {
              dir: direction,
            },
          }}
          ListboxProps={{
            dir: direction,
            sx: {
              direction,
              textAlign: isRTL ? "right" : "left",
            },
          }}
          sx={{
            width: "100%",
            display: "block",
            direction,
            ...sx,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={label}
              variant="outlined"
              size={size}
              dir={direction}
              className={GomakeOutlinedFieldClassName}
              InputLabelProps={getOutlinedFieldInputLabelProps(
                direction,
                params.InputLabelProps
              )}
              inputProps={getOutlinedFieldInputProps(
                direction,
                params.inputProps
              )}
              sx={getOutlinedAutocompleteSx(inputHeight, direction)}
            />
          )}
        />
      </div>
    </ThemeProvider>
  );
};

export { GomakeOutlinedAutocomplete };
